// Program Context Service
// Manages current program context for AI interactions and notifications

import vuexStore from '../store';

interface ProgramContext {
  programId?: string;
  programName?: string;
  programDesc?: string;
  programType?: string;
  pathways?: string[];
  industries?: string[];
  organizers?: any[];
  isActive?: boolean;
}

class ProgramContextService {
  private static currentContext: ProgramContext = {};

  /**
   * Get the current program context from the store
   */
  public static getCurrentProgramContext(): ProgramContext {
    try {
      const programDoc = vuexStore.state.user?.programDoc;

      if (!programDoc) {
        return this.currentContext;
      }

      // Extract program information
      const context: ProgramContext = {
        programId: programDoc._id?.toString(),
        programName: programDoc.programName,
        programDesc: programDoc.programDesc,
        programType: programDoc.programType,
        pathways: programDoc.pathways,
        industries: programDoc.industries,
        organizers: programDoc.organizers,
        isActive: true
      };

      // Cache the context
      this.currentContext = context;

      return context;
    } catch (error) {
      console.warn('🔍 ProgramContextService: Error getting program context:', error);
      return this.currentContext;
    }
  }

  /**
   * Check if user is currently in a program
   */
  public static isUserInProgram(): boolean {
    const context = this.getCurrentProgramContext();
    return !!(context.programId && context.programDesc);
  }

  /**
   * Get program description for AI context
   */
  public static getProgramDescriptionForAI(): string {
    const context = this.getCurrentProgramContext();

    if (!context.programDesc) {
      return '';
    }

    let description = `Program: ${context.programDesc}`;

    if (context.programName) {
      description = `Program "${context.programName}": ${context.programDesc}`;
    }

    if (context.pathways && context.pathways.length > 0) {
      description += `\nPathways: ${context.pathways.join(', ')}`;
    }

    if (context.industries && context.industries.length > 0) {
      description += `\nIndustries: ${context.industries.join(', ')}`;
    }

    return description;
  }

  /**
   * Get formatted program context for AI prompts
   */
  public static getFormattedProgramContext(): string {
    const context = this.getCurrentProgramContext();

    if (!context.programId || !context.programDesc) {
      return '';
    }

    let formatted = `Current Program Context:
- Program Name: ${context.programName || 'Not specified'}
- Program Description: ${context.programDesc}`;

    if (context.pathways && context.pathways.length > 0) {
      formatted += `\n- Pathways: ${context.pathways.join(', ')}`;
    }

    if (context.industries && context.industries.length > 0) {
      formatted += `\n- Industries: ${context.industries.join(', ')}`;
    }

    return formatted;
  }

  /**
   * Clear the current program context
   */
  public static clearContext(): void {
    this.currentContext = {};
  }

  /**
   * Update context manually (useful for testing or edge cases)
   */
  public static updateContext(newContext: Partial<ProgramContext>): void {
    this.currentContext = { ...this.currentContext, ...newContext };
  }
}

export default ProgramContextService;
export type { ProgramContext };
